import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';

const SYNC_TAG = 'today';
const MIN_INTERVAL_MS = 60 * 60 * 1000; // 1 hour

interface PeriodicSyncManager {
  register(tag: string, options?: { minInterval: number }): Promise<void>;
  unregister(tag: string): Promise<void>;
  getTags(): Promise<string[]>;
}

interface SWRegWithPeriodicSync extends ServiceWorkerRegistration {
  periodicSync?: PeriodicSyncManager;
}

export function isPeriodicSyncSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PeriodicSyncManager' in window
  );
}

export async function registerPeriodicSync(): Promise<boolean> {
  if (!isPeriodicSyncSupported()) return false;
  try {
    const reg = (await navigator.serviceWorker.ready) as SWRegWithPeriodicSync;
    if (!reg.periodicSync) return false;
    await reg.periodicSync.register(SYNC_TAG, { minInterval: MIN_INTERVAL_MS });
    return true;
  } catch {
    return false;
  }
}

export async function unregisterPeriodicSync(): Promise<void> {
  if (!isPeriodicSyncSupported()) return;
  try {
    const reg = (await navigator.serviceWorker.ready) as SWRegWithPeriodicSync;
    await reg.periodicSync?.unregister(SYNC_TAG);
  } catch {
    // ignore — SW may not be registered yet
  }
}

/** Listens for PERIODIC_SYNC_TODAY messages from the service worker and
 *  invalidates the ['today'] TanStack Query cache so the snapshot refreshes. */
export function usePeriodicSync() {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    const handler = (event: MessageEvent) => {
      if (event.data?.type === 'PERIODIC_SYNC_TODAY') {
        void queryClient.invalidateQueries({ queryKey: ['today'] });
      }
    };

    navigator.serviceWorker.addEventListener('message', handler);
    return () => navigator.serviceWorker.removeEventListener('message', handler);
  }, [queryClient]);
}
